<template>
    <form class="form-wrapper" @submit.prevent="submitForm">
        <div class="form-group__block">
            <InputGroup
                type="email"
                v-model="email"
                @input="sanitizeEmail"
                @blur="sanitizeEmail"
                :inputName="'email'"
                :placeholder="$t('Your email')"
                required
                :errorMessage="emailError"
                :maxLength="40"
                autocomplete="email"
                inputClass="form-control email-input email"
                customClass="field-signupform-email"
            />
        </div>

        <div class="form-group__block">
            <InputGroup
                v-if="passwordInputType === 'password'"
                type="password"
                v-model="password"
                @input="sanitizePassword"
                @blur="sanitizePassword"
                @keydown="preventSpace"
                :placeholder="$t('Create your password')"
                inputId="signup-form-password"
                required
                :isFocusedProp="isFocusedPassword"
                :maxLength="40"
                :errorMessage="passwordError"
                autocomplete="new-password"
                customClass="field-signupform-password password-container"
                inputClass="form-control password"
            />
            <InputGroup
                v-else
                type="text"
                v-model="password"
                @input="sanitizePassword"
                @blur="sanitizePassword"
                @keydown="preventSpace"
                :placeholder="$t('Create your password')"
                inputId="signup-form-password"
                required
                :isFocusedProp="isFocusedPassword"
                :maxLength="40"
                :errorMessage="passwordError"
                autocomplete="new-password"
                customClass="field-signupform-password password-container"
                inputClass="form-control password"
            />
            <span class="view-password" :class="{active: passwordInputType === 'password'}" @click="togglePasswordVisibility"></span>
        </div>

        <div class="form-group__block">
            <InputGroup
                v-if="passwordConfirmInputType === 'password'"
                type="password"
                v-model="passwordConfirm"
                @input="sanitizePasswordConfirm"
                @blur="sanitizePasswordConfirm"
                @keydown="preventSpace"
                :placeholder="$t('Confirm your password')"
                required
                :isFocusedProp="isFocusedPasswordConfirm"
                :maxLength="40"
                autocomplete="new-password"
                :errorMessage="passwordConfirmError"
                customClass="field-signupform-password password-container"
                inputClass="form-control password"
            />
            <InputGroup
                v-else
                type="text"
                v-model="passwordConfirm"
                @input="sanitizePasswordConfirm"
                @blur="sanitizePasswordConfirm"
                @keydown="preventSpace"
                :placeholder="$t('Confirm your password')"
                required
                :maxLength="40"
                :isFocusedProp="isFocusedPasswordConfirm"
                autocomplete="new-password"
                :errorMessage="passwordConfirmError"
                customClass="field-signupform-password password-container"
                inputClass="form-control password"
            />
            
            <span class="view-password" :class="{active: passwordConfirmInputType === 'password'}" @click="togglePasswordConfirmVisibility"></span>
        </div>

        <div class="login-btns-wrap">
            <button
                type="submit"
                class="button button-accent button-submit btn-hover btn-hover--green"
                :class="{ 'not-changed' : !formValidation }"
            >
                <span v-if="!isSubmitting">
                    {{ $t('Sign Up') }}
                </span>
                <div v-else class="btn-preloader btn-preloader--green"></div>
            </button>
        </div>
        <div class="form-wrapper__link">
            <p>
                {{ $t('Already registered?') }}
                <span @click.prevent="$emit('change-type', 'login')" class="link-class">
                    {{ $t('Log in') }}
                </span>
            </p>
        </div>
    </form>
</template>

<script>
import RequirementMarkers from '/webpack-assets/vue/components/RequirementMarkers.vue';
import InputGroup from '/webpack-assets/vue/components/InputGroup.vue';

export default {
    emits: ['activeStepUpdate', 'update-is-two-fa', 'update-form-loader', 'auth-success', 'updateCaptchaToken', 'captchaToken'],
    components: {
        InputGroup,
        RequirementMarkers,
    },
    props: {
        authData: {
            type: Object,
        }
    },
    data() {
        return {
            email: '',
            username: '',
            password: '',
            passwordConfirm: '',
            emailError: '',
            usernameError: '',
            passwordError: '',
            passwordConfirmError: '',
            passwordInputType: 'password',
            passwordConfirmInputType: 'password',
            formValidation: false,
            isValidMarkers: false,
            isSubmitting: false,
            isFocusedPassword: false,
            isFocusedPasswordConfirm: false,
        }
    },
    methods: {
        sanitizeInput(value) {
            if (value !== undefined && value !== null) {
                let valueString = value.toString();
                return valueString.replace(/[а-яА-ЯёЁ]/g, '');
            }
            return value;
        },
        sanitizeEmail(event) {
            this.email = this.sanitizeInput(this.email);
            this.checkEmailErrors();
            this.checkFormChange();
        },
        checkEmailErrors() {
            const emailPattern = /^[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
            if (this.email.length === 0) {
                this.emailError = this.$t('{title} cannot be blank', { title: this.$t('Email') } );
            } else if (!emailPattern.test(this.email)) {
                this.emailError = this.$t('Email is not a valid email address');
            } else {
                this.emailError = '';
            }
            this.checkCountError();
        },
        sanitizePassword(event) {
            const passwordPattern = /^(?=.*\d)|(?=.*(_|[^\w])).+$/;
            this.password = this.sanitizeInput(this.password);
            if (this.password.length === 0) {
                this.isFocusedPassword = false;
                this.passwordError = this.$t('{title} cannot be blank', { title: this.$t('Password') });
            } else if (this.password.length < 8) {
                this.isFocusedPassword = true;
                this.passwordError = this.$t('At least {cnt} characters', { cnt: 8 });
            } else if (!passwordPattern.test(this.password)) {
                this.isFocusedPassword = true;
                this.passwordError = this.$t('At least one number (0-9) or special symbol');
            } else if (typeof this.password !== 'string' || this.password.length > 32) {
                this.isFocusedPassword = true;
                this.passwordError = this.$t('{title} should contain at most 32 characters', { title: this.$t('Password') });
            } else {
                this.isFocusedPassword = true;
                this.passwordError = '';
            }
            this.checkFormChange();
            this.checkCountError();
        },
        sanitizePasswordConfirm(event) {
            this.passwordConfirm = this.sanitizeInput(this.passwordConfirm); 

            if (this.passwordConfirm.length === 0) {
                this.isFocusedPasswordConfirm = false;
                this.passwordConfirmError = this.$t('{title} cannot be blank', { title: this.$t('Confirm password') });
            } else if (this.passwordConfirm !== this.password) {
                this.isFocusedPasswordConfirm = true;
                this.passwordConfirmError = this.$t('Password mismatch');
            } else {
                this.isFocusedPasswordConfirm = true;
                this.passwordConfirmError = '';
            }
            this.checkFormChange();
            this.checkCountError();
        },
        checkCountError() {
            const errors = [
                this.emailError,
                this.passwordError,
                this.passwordConfirmError
            ];

            const count = errors.filter(error => error !== '').length;

            if (count === 3) {
                this.$emit('change-height-block', 51);
            } else if (count === 2) {
                this.$emit('change-height-block', 34);
            } else if (count === 1) {
                this.$emit('change-height-block', 17);
            } else {
                this.$emit('change-height-block', 0);
            }
        },
        checkFormChange() {
            this.formValidation = 
                this.email.trim().length && 
                !this.emailError &&
                this.password.trim().length &&
                this.passwordConfirm.trim().length &&
                this.password == this.passwordConfirm &&
                !this.passwordError &&
                !this.passwordConfirmError;
        },
        handleValidationStatus(isValid) {
            this.isValidMarkers = isValid;
        },
        togglePasswordVisibility() {
            this.passwordInputType = this.passwordInputType == 'password' ? 'text' : 'password';
        },
        togglePasswordConfirmVisibility() {
            this.passwordConfirmInputType = this.passwordConfirmInputType == 'password' ? 'text' : 'password';
        },
        async submitForm() {
            this.sanitizeEmail()
            this.sanitizePassword()
            this.sanitizePasswordConfirm()
            if (this.isSubmitting || !this.formValidation) return;
            this.isSubmitting = true;
            const tokenInputs = document.querySelectorAll('input[name="cf-turnstile-response"]');
            const turnstileTokenInput = tokenInputs.length ? tokenInputs[tokenInputs.length - 1] : null;

            const formData = new FormData();
            formData.append('SignupForm[email]', this.email);
            formData.append('SignupForm[password]', this.password);
            formData.append('SignupForm[confirm_password]', this.passwordConfirm);

            if (turnstileTokenInput && turnstileTokenInput.value !== '') {
                formData.append('SignupForm[recaptcha]', turnstileTokenInput.value);
            } else {
                this.isSubmitting = false;
                return;
            }

            if(this.email !== '' && this.password !== '' && this.passwordConfirm !== '') {
                this.$emit('captcha-token', true);
            }

            try {
                const response = await this.$request(`/${this.authData?.language}/auth-api/modal-signup`, {
                    method: 'POST',
                    headers: {
                        'X-CSRF-Token': this.authData.csrf,
                    },
                    body: formData
                });

                const resp = await response.json();
                this.$emit('captcha-token', false);
                if (resp.status === 'error') {
                    this.$emit('update-captcha-token');
                    this.emailError = resp.errors.email ? resp.errors.email[0] : '';
                    this.passwordError = resp.errors.password ? resp.errors.password[0] : '';
                    this.passwordConfirmError = resp.errors.confirm_password ? resp.errors.confirm_password[0] : '';
                    this.checkCountError();
                } else if (resp.status === 'success') {
                    this.checkCountError();
                    window.tagTracking("registration")
                    if (resp.isTwoFA) {
                        this.$emit('update-is-two-fa', { status: true, type: 'signup', emailUser: resp.email, ttl: resp.ttl, all: 3, current: 3 });
                        return;
                    }
                    this.$emit('auth-success', resp);
                    this.$emit('update-form-loader', true);
                }
            } finally {
                this.isSubmitting = false;
                setTimeout(() => {
                    this.$emit('captcha-token', false);
                }, 2500);
            }
        }
    }
}
</script>

<style scoped lang="scss">
    .form-wrapper{
        .form-input {
            &.email-input {
                padding-left: 48px !important;
            }
        }
        .button-submit{
            margin-top: 16px !important;
        }
    }
    .link-class {
        color: #4e8aff !important;
        transition: color 0.3s;
        &:hover {
            color: #1767FF !important;
        }
    }
</style>